import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';

import { AttachmentRow, AttachmentThumbnail, openAttachment } from '@/components/inbox/message-attachments';
import { Text, useTheme } from '@/components/ui';
import type { MessageAttachment } from '@/services/data-service';
import { isImage } from '@/utils/document-download';

/**
 * A request's files, grouped under the heading each was filed as (#369).
 *
 * The grouping IS the point. A reviewer opening an awning request wants "the photos" and
 * "the manufacturer's literature" as two things, because they answer two different
 * questions — what does the property look like now, and what exactly is being installed.
 * One undifferentiated strip of thumbnails makes them hunt.
 *
 * Order is fixed rather than alphabetical: photos first because they are what a board
 * looks at first, then the documents that back them up, then anything unclassified.
 */

const HEADING: Record<string, string> = {
	photo: 'Photos',
	before_photo: 'Before Photos',
	after_photo: 'After Photos',
	literature: 'Product Literature',
	quote: 'Quotes & Contracts',
	plan: 'Plans & Drawings',
	insurance: 'Insurance',
	receipt: 'Receipts',
	invoice: 'Invoices',
	misc: 'Other Files',
};

const ORDER = [
	'photo', 'before_photo', 'after_photo',
	'literature', 'plan', 'quote', 'insurance', 'receipt', 'invoice', 'misc',
];

export interface ApplicationAttachmentsProps {
	attachments: MessageAttachment[];
	onError: (error: unknown, fallback: string) => void;
	/** Opens the re-file dialog. Omitted when the reader may not edit. */
	onEdit?: (attachment: MessageAttachment) => void;
	/**
	 * Removes a file from the request. Omitted when the reader may not edit.
	 *
	 * Needed because inbound mail brings things nobody chose to attach — a sender's
	 * signature logo being the usual one — and until now the only options were to relabel
	 * it or live with it.
	 */
	onRemove?: (attachment: MessageAttachment) => void;
	/** Opens the file picker. Omitted when the reader may not edit. */
	onAdd?: () => void;
	/** An upload is in flight — the button says so rather than looking dead. */
	adding?: boolean;
}

export const ApplicationAttachments: React.FC<ApplicationAttachmentsProps> = ({
	attachments, onError, onEdit, onRemove, onAdd, adding = false,
}) => {
	const theme = useTheme();

	const groups = useMemo(() => {
		const byType = new Map<string, MessageAttachment[]>();
		for (const attachment of attachments) {
			const key = attachment.attachmentType || 'misc';
			const list = byType.get(key);
			if (list) list.push(attachment);
			else byType.set(key, [attachment]);
		}
		// Known types in the fixed order, then anything the form invented, so a new label
		// shows up rather than disappearing.
		const keys = [
			...ORDER.filter((k) => byType.has(k)),
			...[...byType.keys()].filter((k) => !ORDER.includes(k)).sort(),
		];
		return keys.map((key) => ({ key, items: byType.get(key) ?? [] }));
	}, [attachments]);

	/**
	 * An empty request still renders, so long as there is something to DO with it. This used
	 * to return null on zero files, which was fine when the section was read-only — but an
	 * Add button that only appears once a file exists cannot be used to add the first one.
	 */
	if (attachments.length === 0 && !onAdd) return null;

	const open = (attachment: MessageAttachment) => {
		openAttachment(attachment).catch((error) => onError(error, 'Could not open that file.'));
	};

	const addButton = onAdd ? (
		<Pressable
			onPress={onAdd}
			disabled={adding}
			testID="btn-add-attachment"
			style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 8 }}
		>
			<Ionicons
				name="cloud-upload-outline"
				size={16}
				color={adding ? theme.colors.onSurfaceVariant : theme.colors.primary}
				style={{ marginRight: 6 }}
			/>
			<Text style={{ color: adding ? theme.colors.onSurfaceVariant : theme.colors.primary, fontWeight: '600' }}>
				{adding ? 'Uploading…' : 'Add file'}
			</Text>
		</Pressable>
	) : null;

	return (
		<View testID="application-attachments">
			<View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
				<Text style={{ fontSize: 18, fontWeight: '700', flex: 1, minWidth: 0 }}>
					Attachments ({attachments.length})
				</Text>
				{addButton}
			</View>
			{attachments.length === 0 ? (
				<Text style={{ color: theme.colors.onSurfaceVariant, fontStyle: 'italic', marginBottom: 4 }}>
					No files on this request yet.
				</Text>
			) : null}
			{groups.map(({ key, items }) => (
				<View key={key} style={{ marginTop: 10 }} testID={`attachment-group-${key}`}>
					<Text
						style={{
							fontSize: 12, fontWeight: '700', letterSpacing: 0.4,
							color: theme.colors.onSurfaceVariant, marginBottom: 6,
						}}
					>
						{(HEADING[key] ?? key.replace(/_/g, ' ')).toUpperCase()} ({items.length})
					</Text>
					{/* Stacked full-width, not a grid: on a request the photo is the submission. */}
					{items.filter((a) => isImage(a.fileType)).map((a) => (
						<View key={a.id}>
							<AttachmentThumbnail attachment={a} onOpen={() => open(a)} fullWidth />
							{onEdit ? (
								<Pressable
									onPress={() => onEdit(a)}
									testID={`btn-edit-attachment-${a.id}`}
									style={{ alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 4, marginTop: -8, marginBottom: 10 }}
								>
									<Text style={{ color: theme.colors.primary, fontWeight: '600', fontSize: 12 }}>
										Edit details{a.checklistKey ? ' · answers a requirement' : ''}
									</Text>
								</Pressable>
							) : null}
							{onRemove ? (
								<Pressable
									onPress={() => onRemove(a)}
									testID={`btn-remove-attachment-${a.id}`}
									style={{ alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 4, marginTop: -8, marginBottom: 10 }}
								>
									<Text style={{ color: theme.colors.error, fontWeight: '600', fontSize: 12 }}>Remove</Text>
								</Pressable>
							) : null}
						</View>
					))}
					{items.filter((a) => !isImage(a.fileType)).map((a) => (
						<View key={a.id}>
							<AttachmentRow attachment={a} onOpen={() => open(a)} />
							{onEdit ? (
								<Pressable
									onPress={() => onEdit(a)}
									testID={`btn-edit-attachment-${a.id}`}
									style={{ alignSelf: 'flex-start', paddingVertical: 4, paddingHorizontal: 4, marginTop: -4, marginBottom: 8 }}
								>
									<Text style={{ color: theme.colors.primary, fontWeight: '600', fontSize: 12 }}>Edit details</Text>
								</Pressable>
							) : null}
							{onRemove ? (
								<Pressable
									onPress={() => onRemove(a)}
									testID={`btn-remove-attachment-${a.id}`}
									style={{ alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 4, marginTop: -8, marginBottom: 10 }}
								>
									<Text style={{ color: theme.colors.error, fontWeight: '600', fontSize: 12 }}>Remove</Text>
								</Pressable>
							) : null}
						</View>
					))}
				</View>
			))}
		</View>
	);
};

export default ApplicationAttachments;
